import apiClient from './api';
import type { User, UpdateUserData } from '@/types/user';

export type { User };

export interface UpdateUserProfileDto {
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export interface UpdatePasswordDto {
  oldPassword: string;
  newPassword: string;
}

export interface CreateInviteDto {
  email: string;
  role:
    | 'ADMIN'
    | 'AGENT'
    | 'CLIENT'
    | 'OWNER'
    | 'SUPER_ADMIN'
    | 'PROPERTY_MANAGER'
    | 'LANDLORD'
    | 'VENDOR'
    | 'TENANT';
  /** Required when the inviting user is SUPER_ADMIN (has no org of their own). */
  organisationId?: number;
}

export interface AcceptInviteDto {
  password: string;
}

export const usersService = {
  // Get current user profile
  async getProfile(): Promise<User> {
    const response = await apiClient.get('/user/data');
    return response.data;
  },

  // Update user profile
  async updateProfile(profileData: UpdateUserProfileDto): Promise<User> {
    const response = await apiClient.patch('/user/profile', profileData);
    return response.data;
  },

  // Update user password
  async updatePassword(passwordData: UpdatePasswordDto): Promise<void> {
    await apiClient.patch('/user/profile/password', passwordData);
  },

  // Delete user data (GDPR compliance)
  async deleteUserData(): Promise<void> {
    await apiClient.delete('/user/data');
  },

  // Admin: Create invite
  async createInvite(inviteData: CreateInviteDto): Promise<void> {
    await apiClient.post('/admin/invites', inviteData);
  },

  // Get invite details by token
  async getInviteDetails(token: string): Promise<{ email: string; role: string }> {
    const response = await apiClient.get(`/auth/invites/${token}`);
    return response.data;
  },

  // Accept invite
  async acceptInvite(token: string, acceptData: AcceptInviteDto): Promise<unknown> {
    const response = await apiClient.post(`/auth/invites/${token}/accept`, acceptData);
    return response.data;
  },

  // Get onboarding state
  async getOnboardingState(): Promise<{ step: string; role: string }> {
    const response = await apiClient.get('/auth/onboarding-state');
    return response.data;
  },

  // Admin: Get all users
  async getUsers(role?: string): Promise<User[]> {
    const params = role ? { role } : {};
    const response = await apiClient.get('/admin/users', { params });
    return response.data;
  },

  // Admin: Get user by ID
  async getUserById(id: number): Promise<User> {
    const response = await apiClient.get(`/admin/users/${id}`);
    return response.data;
  },

  // Admin: Update user
  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await apiClient.patch(`/admin/users/${id}`, data);
    return response.data;
  },

  // Admin: Delete user
  async deleteUser(id: number): Promise<void> {
    await apiClient.delete(`/admin/users/${id}`);
  },
};
